import React from 'react';

/** Outer shell for list pages (matches Projects). */
export const ListPageRoot: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className = '',
}) => (
  <div className={`relative flex h-auto min-h-0 w-full flex-col overflow-x-hidden font-display ${className}`}>
    {children}
  </div>
);

/** KPI / summary card grid. */
export const ListPageMetrics: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className = '',
}) => (
  <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4 lg:p-5 ${className}`}>
    {children}
  </div>
);

/** Single metric card surface. */
export const ListPageMetricCard: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className = '',
}) => (
  <div
    className={`flex flex-col gap-2 rounded-xl p-5 bg-elevation-dark border border-border-dark shadow-xl ${className}`}
  >
    {children}
  </div>
);

/** Search row: search + filter slot(s). */
export const ListPageToolbar: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className = '',
}) => (
  <div className={`px-4 lg:px-5 pb-4 flex flex-col md:flex-row gap-4 items-center ${className}`}>{children}</div>
);

/** Pill strip for filters (horizontal scroll on small screens). */
export const ListPageFilterRow: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className = '',
}) => (
  <div className={`flex gap-2 w-full md:w-auto overflow-x-auto no-scrollbar py-1 ${className}`}>{children}</div>
);

export const listPageFilterPillActive = 'bg-primary text-white font-semibold shadow-md';
export const listPageFilterPillInactive =
  'bg-elevation-dark border border-border-dark text-slate-300 hover:border-slate-500';

/** Desktop table container (`md` = show from tablet up; `lg` = show from large screens only). */
export const ListPageTableSurface: React.FC<{
  children: React.ReactNode;
  className?: string;
  breakpoint?: 'md' | 'lg';
}> = ({ children, className = '', breakpoint = 'md' }) => (
  <div
    className={`${
      breakpoint === 'lg' ? 'hidden lg:block' : 'hidden md:block'
    } bg-elevation-dark rounded-xl border border-border-dark shadow-2xl overflow-hidden ${className}`}
  >
    {children}
  </div>
);

/** Padded section wrapping desktop table + mobile stack. */
export const ListPageContentSection: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className = '',
}) => <div className={`px-4 lg:px-5 pb-5 ${className}`}>{children}</div>;

/** Mobile card list column (pair with `ListPageTableSurface`: use `breakpoint="lg"` for `lg:hidden` here). */
export const ListPageMobileStack: React.FC<{
  children: React.ReactNode;
  className?: string;
  /** When `lg`, hidden on large screens only (matches `ListPageTableSurface breakpoint="lg"`). */
  breakpoint?: 'md' | 'lg';
}> = ({ children, className = '', breakpoint = 'md' }) => (
  <div
    className={`${
      breakpoint === 'lg' ? 'lg:hidden' : 'md:hidden'
    } flex flex-col gap-3 ${className}`}
  >
    {children}
  </div>
);

/** Inline error below toolbar. */
export const ListPageInlineError: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="mx-4 lg:mx-5 rounded-xl border border-red-500/40 bg-red-500/20 px-4 py-2 text-red-400 text-sm">
    {children}
  </div>
);

/** Table header row (Projects style). */
export const ListPageTableHeadRow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <tr className="bg-sidebar-dark/50 border-b border-border-dark">{children}</tr>
);

/** Table header cell. */
export const ListPageTh: React.FC<React.ThHTMLAttributes<HTMLTableCellElement>> = ({
  children,
  className = '',
  ...rest
}) => (
  <th
    className={`px-5 py-4 text-[10px] font-bold uppercase tracking-widest text-slate-500 ${className}`}
    {...rest}
  >
    {children}
  </th>
);

/** Table body default row divider. */
export const listPageTableBodyClass = 'divide-y divide-border-dark/50';

/** Default row hover. */
export const listPageTableRowClass = 'hover:bg-white/[0.02] transition-colors group';

/** Empty / loading cell inside table surface. */
export const ListPageTablePlaceholder: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="px-5 py-5 text-center text-slate-400">{children}</div>
);

/** Mobile empty / loading card. */
export const ListPageMobilePlaceholder: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="bg-elevation-dark rounded-xl p-5 border border-border-dark shadow-lg text-center text-slate-400">
    {children}
  </div>
);
